import { isNil } from 'lodash-es';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { CrosshairIcon } from '@dynatrace/strato-icons';

import { allowWhenKeyIsEnter } from './utils.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { KeyboardControls } from '../../../core/components/interactions-overlay/constants.js';
import { INTERACTION_MODE } from '../../../core/components/interactions-overlay/types.js';
import { calculateInitialSelectionAreaPositions } from '../../../core/components/interactions-overlay/utils/calculate-initial-selection-area-positions.js';
import { reduceSelectionAreaVariant } from '../../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { useOverlay } from '../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useSynchronizedSelection } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useToolbarContext } from '../../../core/components/toolbar/hooks/useToolbarContext.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { getPercentageFromPixel } from '../../../core/utils/get-percentage-from-pixel.js';
import { XYCHART_MESSAGES } from '../../messages/messages.js';
import { reduceInteractionMode } from '../../store/reduce-interaction-mode.js';
import {
  reduceExploreCancelAll,
  reduceExploreSelect,
} from '../../store/reducer-explorer.js';
import {
  useInteractionModeState,
  useSelectState,
  useSetState,
} from '../../store/selectors.js';

export const ExploreButton = (props: { placement: 'menu' | 'auto' }) => {
  const setState = useSetState();
  const exploreButtonRef = useRef<HTMLButtonElement>(null);
  const tabIndex = useA11yToolbarTabIndex(INTERACTION_MODE.Explore);
  const intl = useIntl();
  const interactionMode = useInteractionModeState();
  const { overlayRef: chartRef } = useOverlay();
  const { width } = useGridPlotSize();
  const { resetSelectionState } = useSynchronizedSelection();
  const { updateLastItemSelected } = useToolbarContext();
  const selectState = useSelectState();

  const isSelected = interactionMode === INTERACTION_MODE.Explore;

  const createInitialSelectionArea = () => {
    if (!isNil(chartRef.current)) {
      const bounds = chartRef.current.getBoundingClientRect();

      const [start, end] = calculateInitialSelectionAreaPositions(bounds);

      setState(
        reduceExploreSelect({
          start,
          end,
          finished: true,
          accessible: true,
          variant: 'select',
          isKeyboardCreated: true,
          selectionPercentage: getPercentageFromPixel([start, end], width),
        }),
      );
    }
  };

  const handleClick = () => {
    if (interactionMode !== 'explore') {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    }

    if (selectState?.start && selectState?.end) {
      if (selectState?.isKeyboardCreated) {
        reduceSelectionAreaVariant('select');
      } else {
        resetSelectionState?.();
        setState(reduceExploreCancelAll);
      }
    }

    updateLastItemSelected(INTERACTION_MODE.Explore);
  };

  const handleKeyDown = () => {
    setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    createInitialSelectionArea();
  };

  return (
    <ChartToolbarRenderer.Control
      ref={exploreButtonRef}
      prefixIcon={<CrosshairIcon />}
      tooltipText={intl.formatMessage(XYCHART_MESSAGES.zoomModeExplore)}
      tooltipKeyShortcut={
        KeyboardControls.KEYBOARD_EXPLORE_MODE_SHORTCUT_ALTERNATIVE
      }
      selected={isSelected}
      onKeyDown={allowWhenKeyIsEnter(handleKeyDown)}
      onClick={handleClick}
      tabIndex={tabIndex}
      placement={props.placement}
      data-dt-name="ToolbarExploreMode"
    />
  );
};

ExploreButton['displayName'] = 'ExploreButton';
